import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Modal } from '../../../design-system';
import { OS, OS_LABEL } from '../../../lib/platform';
import { apiClient } from '../../../auth-lib';
import './AgentInstallHelp.css';

// Shown when the hrms-agent:// launch produced nothing — the agent is not
// installed on this computer yet.
//
// ⚠️ WHAT THIS SCREEN USED TO BE, AND WHY IT CHANGED. It listed terminal
// commands: install Node, unzip the agent somewhere permanent, `npm install`
// (which compiled two C++ addons and therefore wanted Visual Studio Build Tools
// on Windows), then a setup command with an access token pasted into it. That is
// developer-grade setup handed to an accountant, and no amount of nicer prose
// fixes it — people simply stopped at step one.
//
// The agent is now a single file that installs itself. This screen is a download
// button and a progress indicator, and the only thing the user does is
// double-click what they downloaded.
//
// ⚠️ HONEST LIMIT, stated here because it drives the whole design: a website
// cannot install software. No browser has an API or a permission for it, by
// design. "Download, double-click, done" is the closest thing that exists, so
// that is what this builds — and everything AFTER the double-click (installing,
// registering the launcher, enrolling this machine, resuming the login) is
// automatic.

const POLL_MS = 2000;

// ⚠️ WINDOWS HAS TWO DIFFERENT BLOCKS AND ONLY ONE OF THEM CAN BE CLICKED PAST.
// This step used to name only SmartScreen — "More info, then Run anyway" — which
// is impossible advice under Smart App Control: that dialog has no Run anyway
// button at all, only "Okay" and "Get apps from the Store". Telling somebody to
// press a button that is not there makes them think they did it wrong, which is
// the worst outcome for a step in a setup wizard.
const STEPS = {
  [OS.WINDOWS]: [
    'Download the file below.',
    'Double-click it. If Windows says the publisher is unknown, choose More info, then Run anyway.',
    'That is all. This page continues on its own.',
  ],
  [OS.MAC]: [
    'Download the file below and open it.',
    'Right-click the app and choose Open, then confirm. (Opening it normally the first time is blocked because it is not signed yet.)',
    'That is all. This page continues on its own.',
  ],
  [OS.LINUX]: [
    'Download the file below.',
    'Mark it executable and run it once.',
    'That is all. This page continues on its own.',
  ],
};

const DOWNLOAD_PLATFORM = { [OS.WINDOWS]: 'windows', [OS.MAC]: 'mac', [OS.LINUX]: 'linux' };

// Windows-only for now — the agent is only built and distributed for Windows,
// so offering Mac/Linux tabs here would just be dead ends.
const tab = OS.WINDOWS;

// `mode` decides what "finished" means, and it has to.
//
// ⚠️ ON A REINSTALL, "AN AGENT EXISTS" IS TRUE BEFORE ANYTHING HAPPENS. The
// watcher below polls until the user has a machine — perfect for a first setup,
// and a lie for a repair: the broken machine is already in that list, so the
// dialog would report success the instant it opened, having repaired nothing.
// A repair is finished when a machine registers ANEW, so the baseline taken at
// open is what the poll compares against.
function AgentInstallHelp({ onClose, onReady, mode = 'install' }) {
  const [manifest, setManifest] = useState(null);
  const [phase, setPhase] = useState('idle'); // idle | waiting | enrolling | ready | error
  const [error, setError] = useState('');
  const timers = useRef([]);

  useEffect(() => {
    apiClient
      .get('/api/agent/manifest')
      .then((res) => setManifest(res.data))
      .catch(() => setManifest({ available: {} }));
  }, []);

  // Everything scheduled here must be cancellable: the user can close this
  // dialog mid-poll, and a timer that fires afterwards would setState on an
  // unmounted component and, worse, keep launching the agent.
  useEffect(
    () => () => {
      timers.current.forEach(clearTimeout);
      timers.current = [];
    },
    [],
  );

  /**
   * Wait for the machine to register itself, then finish setup.
   *
   * ⚠️ The browser cannot see whether an installer ran — a hrms-agent:// launch
   * is fire-and-forget and reports nothing back. The only honest signal is the
   * SERVER noticing a new agent for this user, so that is what we watch. It also
   * means this works when the user installs the agent from a different window,
   * or walks away and comes back.
   */
  const beginWatching = useCallback(async () => {
    setPhase('waiting');
    setError('');

    const deadline = Date.now() + 10 * 60 * 1000;

    // Taken BEFORE the launch, so "something registered since I pressed this"
    // is answerable. Registering again refreshes `registeredAt` on the same row
    // (registerAgent upserts on the fingerprint), so repairing the machine you
    // are sitting at is detected without needing a second row to appear.
    let baseline = 0;
    try {
      const { data } = await apiClient.get('/api/credentials/agents/mine');
      baseline = Math.max(0, ...(data.agents || []).map((a) => new Date(a.registeredAt || 0).getTime()));
    } catch {
      // No baseline is the safe direction here: 0 means any registration counts,
      // which at worst finishes the dialog early rather than hanging on a repair
      // that did work.
    }

    const tick = async () => {
      if (Date.now() > deadline) {
        setPhase('error');
        setError('Setup did not finish. If you have run the installer, close this and press Login again.');
        return;
      }
      try {
        const { data } = await apiClient.get('/api/credentials/agents/mine');
        const newest = Math.max(0, ...(data.agents || []).map((a) => new Date(a.registeredAt || 0).getTime()));
        if (data.agents?.length && newest > baseline) {
          setPhase('ready');
          // Hand back to the caller so the login the user originally asked for
          // can continue by itself — the point is that setup is invisible.
          onReady?.();
          return;
        }
      } catch {
        // A blip while polling is not a failure; the deadline is the failure.
      }
      timers.current.push(setTimeout(tick, POLL_MS));
    };

    // The installed agent still has to be told WHO it belongs to. Mint the
    // one-time setup link and let the launcher do it — there is nothing for the
    // user to copy, and the token cannot do anything except register a machine.
    try {
      const { data } = await apiClient.post('/api/credentials/agents/enroll-token');
      setPhase('enrolling');
      window.location.href = `hrms-agent://enroll?token=${encodeURIComponent(data.token)}`;
    } catch {
      // If the link cannot be minted we still poll: the user may be part-way
      // through installing, and pressing Login again re-mints it.
    }
    timers.current.push(setTimeout(tick, POLL_MS));
  }, [onReady]);

  const platformKey = DOWNLOAD_PLATFORM[tab];
  const available = manifest?.available?.[platformKey];
  // The Smart App Control fallback. Only Windows has one, and only when this
  // server has actually published it.
  const portable = manifest?.available?.['windows-portable'];
  const portableUrl = `${apiClient.defaults.baseURL || ''}/api/agent/download?platform=windows-portable`;
  const downloadUrl = `${apiClient.defaults.baseURL || ''}/api/agent/download?platform=${platformKey}`;

  const onDownload = () => {
    // Navigating to the URL is what starts the download; the watch begins at the
    // same moment so the user never has to come back and tell us they are done.
    beginWatching();
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={mode === 'reinstall' ? 'Repair this computer' : 'Set up automatic login'}
      footer={
        <Button type="button" variant="secondary" onClick={onClose}>
          {phase === 'ready' ? 'Done' : 'Close'}
        </Button>
      }
    >
      {/* ⚠️ THIS COPY IS A PROMISE AND IT HAS TO STAY TRUE. It used to say the
          program "is not a background service and holds nothing open", which was
          exactly right when it only typed passwords — and became false the day
          installing also registered the Tally sync service to start at login.
          Copy that quietly stops matching what the software does is worse than
          no copy: the person who read it is the one who will be surprised. */}
      <p className="agent-intro">
        {mode === 'reinstall'
          ? 'Installing again repairs this computer: it restores the launcher, re-registers the sync service and starts it. Nothing you have saved is affected.'
          : 'This needs a small program on this computer. It does two things: it types a portal’s username and password into a normal Chrome window when you press Login, and — if you connect a client’s Tally — it reads those books on a schedule.'}
      </p>
      {mode !== 'reinstall' && (
        <p className="agent-intro">
          The login half runs only when you ask for it. The Tally half starts
          with Windows and checks for work every few minutes; it reads nothing
          until you connect a client’s books, and you can remove it at any time.
        </p>
      )}

      <ol className="agent-steps">
        {STEPS[tab].map((step) => (
          <li key={step}>{step}</li>
        ))}
      </ol>

      {/* ⚠️ THE BLOCK WITH NO WAY PAST IT, said before it happens rather than
          left as a dead end. Smart App Control is a different thing from
          SmartScreen: it refuses anything whose publisher it cannot verify and
          offers only "Okay" and "Get apps from the Store" — there is no Run
          anyway. Turning it off is also a ONE-WAY DOOR on Windows, which is not
          a decision to discover halfway through a setup wizard. */}
      {tab === OS.WINDOWS && (
        <details className="agent-note">
          <summary>Windows blocked it and there was no “Run anyway”</summary>
          <p>
            That is <strong>Smart App Control</strong>, not the usual warning. It refuses
            any program whose publisher it cannot verify, and it has no override — this
            installer is not code-signed yet, so it is caught by it.
          </p>
          {/* ⚠️ THE FALLBACK IS OFFERED HERE, NOT DESCRIBED. Somebody reading
              this has already been blocked once; a paragraph telling them a
              different build exists somewhere is not help. The portable build
              ships the Node runtime unmodified — still carrying its OpenJS
              Foundation signature — so Smart App Control has a publisher it
              already trusts. Our code rides beside it as an ordinary script. */}
          {portable ? (
            <>
              <p>
                <strong>Use the portable version instead.</strong> It runs the official,
                signed Node.js runtime rather than a program of ours, so there is nothing
                for Windows to refuse. It takes two extra steps: extract the folder, then
                double-click <code>Install.cmd</code> inside it.
              </p>
              <div className="agent-download">
                <a
                  className="ds-btn ds-btn-secondary ds-btn-md"
                  href={portableUrl}
                  download
                  onClick={onDownload}
                >
                  Download the portable version
                </a>
                <span className="agent-filesize">
                  {portable.name} · {(portable.bytes / 1024 / 1024).toFixed(0)} MB
                </span>
              </div>
            </>
          ) : (
            <p>
              A portable version that avoids this exists but has not been built on this
              server yet. Ask whoever manages HRMS to run <code>npm run package</code> on
              a Windows machine.
            </p>
          )}
          <p>
            Otherwise: install on a computer that does not have Smart App Control on — it
            is only on by default on Windows 11 machines that were <em>clean-installed</em>,
            so most upgraded PCs and all Windows 10 PCs are fine — or turn it off in
            <strong> Windows Security → App &amp; browser control → Smart App Control
            settings</strong>.
          </p>
          <p className="agent-warning">
            Turning it off cannot be undone: Windows will not let you switch Smart App
            Control back on again without reinstalling Windows. Try the portable version
            or another computer first.
          </p>
        </details>
      )}

      {manifest && !available ? (
        <p className="agent-warning">
          The agent has not been built for {OS_LABEL[tab]} yet. Ask whoever
          manages HRMS to run <code>npm run package</code> in the agent folder.
        </p>
      ) : (
        <div className="agent-download">
          {/* A real anchor, not the Button component: Button renders a
              motion.button and has no `as` prop, and the download has to be a
              plain navigation so the browser streams it to disk and can resume
              a dropped connection. It borrows the design system's classes so it
              still looks like every other primary action. */}
          <a className="ds-btn ds-btn-primary ds-btn-md" href={downloadUrl} download onClick={onDownload}>
            Download for {OS_LABEL[tab]}
          </a>
          {available && (
            <span className="agent-filesize">
              {/* ⚠️ THE VERSION IS NAMED, and it is the one fact this line was
                  missing. A file name and a size in megabytes say nothing about
                  WHICH build is about to be installed — and on a screen whose
                  whole purpose is often "update this computer", that is the only
                  thing the person actually needs to check afterwards. The
                  installer now carries it in its own name too, so the file in
                  their Downloads folder still answers the question tomorrow. */}
              {available.version && <strong>Version {available.version}</strong>}
              {available.version ? ' · ' : ''}
              {available.name} · {(available.bytes / 1024 / 1024).toFixed(0)} MB
            </span>
          )}
        </div>
      )}

      {phase !== 'idle' && (
        <div className={`agent-status is-${phase}`} role="status" aria-live="polite">
          {phase === 'waiting' && 'Waiting for you to run the installer…'}
          {phase === 'enrolling' && 'Setting this computer up…'}
          {phase === 'ready' && '✓ Ready. Continuing your login…'}
          {phase === 'error' && error}
        </div>
      )}

      <p className="agent-note">
        Nothing is copied or typed — once the installer has run, this page picks
        it up and carries on by itself.
      </p>
    </Modal>
  );
}

export default AgentInstallHelp;
